import { useState } from "react";
import { ThumbsUp, ThumbsDown, MessageSquare, ChevronLeft, ChevronRight, Reply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Opportunity, IssueFeedback, IssueFeedbackComment } from "@/opportunity/opportunity";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import UsefulFeedbackModal from "@/opportunity/UsefulFeedbackModal";

const COMMENT_LABELS: Record<IssueFeedbackComment, string> = {
    dont_care: "I don't care",
    not_useful: "Not useful",
    known_issue: "Known issue",
    other: "Other",
};

const getUserDisplayName = (user: IssueFeedback["user"]) => {
    if (user.first_name || user.last_name) {
        return `${user.first_name} ${user.last_name}`.trim();
    }
    return user.username;
};

export const FeedbackTimelineEntry = ({ entry, compact }: { entry: IssueFeedback; compact?: boolean }) => {
    const isUseful = entry.useful_meter === "thumbs_up";
    const name = getUserDisplayName(entry.user);

    return (
        <div className={cn("flex items-start gap-2", compact ? "py-1" : "py-2")}>
            <div className={cn(
                "mt-0.5 rounded-full flex items-center justify-center shrink-0",
                compact ? "w-5 h-5" : "w-6 h-6",
                isUseful ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"
            )}>
                {isUseful
                    ? <ThumbsUp className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
                    : <ThumbsDown className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
                }
            </div>
            <div className="flex-1 min-w-0">
                <p className={cn("text-foreground leading-snug", compact ? "text-xs" : "text-sm")}>
                    <span className="font-medium">{name}</span>
                    {" "}
                    <span className="text-muted-foreground">
                        {isUseful ? "found this useful" : "marked this as not useful"}
                        {entry.comment && (
                            <> — <span className="italic">{COMMENT_LABELS[entry.comment]}</span></>
                        )}
                    </span>
                    {entry.text && (
                        <p className={cn("text-muted-foreground mt-1 italic", compact ? "text-[10px]" : "text-xs")}>
                            "{entry.text}"
                        </p>
                    )}
                </p>
                <p className={cn("text-muted-foreground mt-0.5", compact ? "text-[10px]" : "text-xs")}>
                    {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                </p>
            </div>
        </div>
    );
};

interface IssueFeedbackSectionProps {
    opportunity: Opportunity;
    compact?: boolean;
    /** Number of feedback entries to show per page. 0 = show all. */
    pageSize?: number;
    /** Pass e.stopPropagation when used inside a Link wrapper (e.g. OpportunityCard) */
    onInteract?: (e: React.MouseEvent) => void;
}

const IssueFeedbackSection = ({ opportunity, compact = false, pageSize = 0, onInteract }: IssueFeedbackSectionProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(0);

    const feedback = opportunity.feedback ?? [];
    const sortedFeedback = [...feedback].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const isPaginated = pageSize > 0 && sortedFeedback.length > pageSize;
    const totalPages = isPaginated ? Math.ceil(sortedFeedback.length / pageSize) : 1;
    const displayedFeedback = isPaginated
        ? sortedFeedback.slice(page * pageSize, (page + 1) * pageSize)
        : sortedFeedback;

    return (
        <>
            <div className={cn("flex flex-col", compact ? "gap-1" : "gap-3")}>
                {/* Header + Reply button */}
                <div className="flex items-center gap-2">
                    {!compact && (
                        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                            Was this issue useful?
                        </span>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className={cn("gap-1.5", compact && "h-7 px-2 text-xs")}
                        onClick={(e) => {
                            onInteract?.(e);
                            setModalOpen(true);
                        }}
                    >
                        <Reply className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
                        {!compact && "Reply"}
                    </Button>

                    {compact && feedback.length > 0 && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1 ml-1">
                            <MessageSquare className="w-3 h-3" />
                            {feedback.length}
                        </span>
                    )}
                </div>

                {/* Feedback timeline */}
                {sortedFeedback.length > 0 && (
                    <div className={cn("border-t", compact ? "pt-1" : "pt-3")}>
                        {!compact && (
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-2">
                                Feedback history
                            </p>
                        )}
                        <div className={cn(
                            "flex flex-col divide-y divide-border/50",
                            compact && "max-h-32 overflow-y-auto"
                        )}>
                            {displayedFeedback.map((entry) => (
                                <FeedbackTimelineEntry key={entry.id} entry={entry} compact={compact} />
                            ))}
                        </div>
                        {isPaginated && (
                            <div className="flex items-center justify-between pt-2 border-t mt-2">
                                <span className="text-xs text-muted-foreground">
                                    {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedFeedback.length)} of {sortedFeedback.length}
                                </span>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                        disabled={page >= totalPages - 1}
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {modalOpen && (
                <UsefulFeedbackModal
                    opportunity={opportunity}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </>
    );
};

export default IssueFeedbackSection;